import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Search, Plus, Filter, X, ChevronLeft, ChevronRight,
  Handshake, Loader2, Upload, CalendarDays, ClipboardList, FileSpreadsheet, MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { FASES_ORDEN, getFaseColor, getUnidadCodigo } from './constants';
import ImportarEntregasModal from './ImportarEntregasModal';

const FILTROS_KEY = 'entregas_filtros';

const loadFiltros = () => {
  try {
    const saved = localStorage.getItem(FILTROS_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const saveFiltros = (filtros) => {
  localStorage.setItem(FILTROS_KEY, JSON.stringify(filtros));
};

const EntregasUI = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const savedFiltros = loadFiltros();

  const [entregas, setEntregas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [estados, setEstados] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [arquitectos, setArquitectos] = useState([]);

  // Filtros con persistencia
  const [busqueda, setBusqueda] = useState(savedFiltros?.busqueda || '');
  const [filtroProy, setFiltroProy] = useState(savedFiltros?.filtroProy || 'all');
  const [filtroFase, setFiltroFase] = useState(savedFiltros?.filtroFase || 'all');
  const [filtroEstado, setFiltroEstado] = useState(savedFiltros?.filtroEstado || 'all');
  const [filtroArquitecto, setFiltroArquitecto] = useState(savedFiltros?.filtroArquitecto || 'pending_default');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState(savedFiltros?.filtroFechaDesde || '');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState(savedFiltros?.filtroFechaHasta || '');
  const [filtroOrigen, setFiltroOrigen] = useState(savedFiltros?.filtroOrigen || 'all');
  const [filtroChecklist, setFiltroChecklist] = useState(savedFiltros?.filtroChecklist || 'all');
  const [showMoreFilters, setShowMoreFilters] = useState(savedFiltros?.showMoreFilters || false);
  const [page, setPage] = useState(savedFiltros?.page || 1);
  const [pageSize, setPageSize] = useState(savedFiltros?.pageSize || 25);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Persistir filtros en localStorage
  useEffect(() => {
    saveFiltros({
      busqueda, filtroProy, filtroFase, filtroEstado,
      filtroArquitecto, filtroFechaDesde, filtroFechaHasta,
      filtroOrigen, filtroChecklist, showMoreFilters, page, pageSize
    });
  }, [busqueda, filtroProy, filtroFase, filtroEstado,
      filtroArquitecto, filtroFechaDesde, filtroFechaHasta,
      filtroOrigen, filtroChecklist, showMoreFilters, page, pageSize]);

  const loadEstados = useCallback(async () => {
    try {
      const resp = await api.get('/entregas/estados');
      setEstados(resp.data || []);
    } catch (err) {
      console.error('Error loading estados:', err);
    }
  }, []);

  const loadProyectos = useCallback(async () => {
    try {
      const resp = await api.get('/entregas/proyectos-con-entregas');
      setProyectos(resp.data || []);
    } catch (err) {
      console.error('Error loading proyectos:', err);
    }
  }, []);

  const loadArquitectos = useCallback(async () => {
    try {
      const resp = await api.get('/entregas/dashboard/arquitectos');
      const lista = resp.data || [];
      setArquitectos(lista);
      if (filtroArquitecto === 'pending_default') {
        const esArqEntregas = user?.id && lista.some(a => a.id === user.id);
        setFiltroArquitecto(esArqEntregas ? user.id : 'all');
      }
    } catch (err) {
      console.error('Error loading arquitectos:', err);
      if (filtroArquitecto === 'pending_default') setFiltroArquitecto('all');
    }
  }, []);

  const buildFiltroParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filtroProy && filtroProy !== 'all') {
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filtroProy);
      params.append(isUUID ? 'proyecto_id' : 'proyecto_nombre', filtroProy);
    }
    if (filtroFase && filtroFase !== 'all') params.append('fase', filtroFase);
    if (filtroEstado && filtroEstado !== 'all') params.append('estado', filtroEstado);
    if (filtroArquitecto && filtroArquitecto !== 'all') params.append('arquitecto_id', filtroArquitecto);
    if (filtroFechaDesde) params.append('fecha_desde', filtroFechaDesde);
    if (filtroFechaHasta) params.append('fecha_hasta', filtroFechaHasta);
    if (filtroOrigen && filtroOrigen !== 'all') params.append('origen', filtroOrigen);
    if (filtroChecklist === 'incompleto') params.append('checklist_incompleto', 'true');
    if (busqueda.trim()) params.append('busqueda', busqueda.trim());
    return params;
  }, [filtroProy, filtroFase, filtroEstado, filtroArquitecto, filtroFechaDesde,
      filtroFechaHasta, filtroOrigen, filtroChecklist, busqueda]);

  const loadEntregas = useCallback(async () => {
    if (filtroArquitecto === 'pending_default') return;
    setLoading(true);
    try {
      const params = buildFiltroParams();
      params.append('page', page);
      params.append('page_size', pageSize);

      const resp = await api.get(`/entregas/?${params.toString()}`);
      setEntregas(resp.data.data || []);
      setTotal(resp.data.total || 0);
      setTotalPages(resp.data.total_pages || 0);
    } catch (err) {
      console.error('Error loading entregas:', err);
      toast.error('Error al cargar entregas');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filtroArquitecto, buildFiltroParams]);

  const exportarExcel = async () => {
    if (exporting || filtroArquitecto === 'pending_default') return;
    setExporting(true);
    try {
      const resp = await api.get(`/entregas/export/excel?${buildFiltroParams().toString()}`, { responseType: 'blob' });
      const match = /filename="?([^"]+)"?/.exec(resp.headers['content-disposition'] || '');
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'entregas.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Excel exportado: ${resp.headers['x-total-filas'] || 0} entregas`);
    } catch (err) {
      console.error('Error exportando Excel:', err);
      toast.error('No se pudo exportar el Excel');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { loadEstados(); loadProyectos(); loadArquitectos(); }, [loadEstados, loadProyectos, loadArquitectos]);
  useEffect(() => { loadEntregas(); }, [loadEntregas]);

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroProy('all');
    setFiltroFase('all');
    setFiltroEstado('all');
    setFiltroArquitecto('all');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setFiltroOrigen('all');
    setFiltroChecklist('all');
    setPage(1);
    localStorage.removeItem(FILTROS_KEY);
  };

  const getEstadoColor = (estado) => {
    const found = estados.find(e => e.estado === estado);
    return found?.color || '#6B7280';
  };

  const getEstadoLabel = (estado) => {
    const found = estados.find(e => e.estado === estado);
    return found?.estado_label || estado;
  };

  const estadosFiltrados = filtroFase && filtroFase !== 'all'
    ? estados.filter(e => e.fase === filtroFase)
    : estados;

  const hayFiltrosActivos = filtroProy !== 'all' || filtroFase !== 'all' ||
    filtroEstado !== 'all' || (filtroArquitecto && filtroArquitecto !== 'all' && filtroArquitecto !== 'pending_default') || filtroFechaDesde ||
    filtroFechaHasta || filtroOrigen !== 'all' || filtroChecklist !== 'all' || busqueda;

  const accionesSecundarias = [
    { id: 'btn-auditoria', label: 'Auditoría', icon: ClipboardList, onClick: () => navigate('/entregas/auditoria'),
      className: 'border-[#888] text-[#888] hover:bg-[#888]/10' },
    { id: 'btn-calendario', label: 'Calendario', icon: CalendarDays, onClick: () => navigate('/entregas/calendario'),
      className: 'border-[#5B7FA6] text-[#5B7FA6] hover:bg-[#5B7FA6]/10' },
    { id: 'btn-importar-excel', label: 'Importar', icon: Upload, onClick: () => setShowImportModal(true),
      className: 'border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272]/10' },
  ];

  return (
    <div className="space-y-4" data-testid="entregas-ui">
      {/* Header */}
      <div className="flex flex-col 2xl:flex-row 2xl:items-center justify-between gap-3" data-testid="entregas-header">
        <div className="flex items-center gap-2 min-w-0">
          <Handshake size={22} className="text-[#4E7A79] shrink-0" />
          <h1 className="text-xl sm:text-2xl font-bold text-[#2D2D2D] whitespace-nowrap truncate">Entregas Inmobiliarias</h1>
          <Badge variant="secondary" className="ml-2 shrink-0" data-testid="entregas-total">{total}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="entregas-acciones">
          <div className="hidden md:flex flex-wrap gap-2">
            {accionesSecundarias.map(a => (
              <Button key={a.id} variant="outline" onClick={a.onClick} className={a.className} data-testid={a.id}>
                <a.icon size={16} className="mr-1" />
                {a.label}
              </Button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={exportarExcel}
            disabled={exporting}
            className="flex-1 md:flex-none border-[#4E7A79] text-[#4E7A79] hover:bg-[#4E7A79]/10"
            data-testid="export-excel-btn"
          >
            {exporting
              ? <Loader2 size={16} className="mr-1 animate-spin" data-testid="export-excel-spinner" />
              : <FileSpreadsheet size={16} className="mr-1" />}
            {exporting ? 'Exportando…' : 'Exportar Excel'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="md:hidden px-3" aria-label="Más acciones" data-testid="btn-mas-acciones">
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="menu-mas-acciones">
              {accionesSecundarias.map(a => (
                <DropdownMenuItem key={a.id} onClick={a.onClick} data-testid={`menu-${a.id}`}>
                  <a.icon size={16} className="mr-2" />
                  {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            onClick={() => navigate('/entregas/nueva')}
            className="w-full md:w-auto order-first md:order-none bg-[#9d8272] hover:bg-[#8a7162] text-white"
            data-testid="btn-nueva-entrega"
          >
            <Plus size={16} className="mr-1" />
            Nueva Entrega
          </Button>
        </div>
      </div>

      {/* Filtros principales */}
      <Card className="bg-white border border-[#E8E8E7]">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            {/* Fila 1: Búsqueda + Proyecto + Fase + Arquitecto */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_0.7fr_0.6fr_0.7fr] gap-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
                <Input
                  placeholder="Buscar código, proforma, unidad, cliente..."
                  value={busqueda}
                  onChange={(e) => { setBusqueda(e.target.value); setPage(1); }}
                  className="pl-9 text-sm"
                  data-testid="filtro-busqueda"
                />
              </div>
              <Select value={filtroProy} onValueChange={(v) => { setFiltroProy(v); setPage(1); }}>
                <SelectTrigger className="text-sm" data-testid="filtro-proyecto">
                  <SelectValue placeholder="Proyecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los proyectos</SelectItem>
                  {proyectos.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="flex items-center gap-1.5">
                        {p.nombre}
                        {!p.registrado && (
                          <span className="inline-flex items-center px-1 py-0 rounded text-[8px] font-semibold bg-red-100 text-red-600 leading-tight">
                            Sin registro
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filtroFase} onValueChange={(v) => { setFiltroFase(v); setFiltroEstado('all'); setPage(1); }}>
                <SelectTrigger className="text-sm" data-testid="filtro-fase">
                  <SelectValue placeholder="Fase" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las fases</SelectItem>
                  {FASES_ORDEN.map(f => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filtroArquitecto} onValueChange={(v) => { setFiltroArquitecto(v); setPage(1); }}>
                <SelectTrigger className="text-sm" data-testid="filtro-arquitecto">
                  <SelectValue placeholder="Arquitecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los arquitectos</SelectItem>
                  {arquitectos.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Botón + Más filtros */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                className="text-xs text-[#9d8272] hover:text-[#8a7162] p-0 h-auto"
                data-testid="btn-mas-filtros"
              >
                <Filter size={14} className="mr-1" />
                {showMoreFilters ? '- Menos filtros' : '+ Más filtros'}
              </Button>
              {hayFiltrosActivos && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={limpiarFiltros}
                  className="text-xs text-red-500 hover:text-red-700 p-0 h-auto"
                  data-testid="btn-limpiar-filtros"
                >
                  <X size={14} className="mr-1" />
                  Limpiar filtros
                </Button>
              )}
            </div>

            {/* Filtros expandidos */}
            {showMoreFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 pt-2 border-t border-[#E8E8E7]">
                <Select value={filtroEstado} onValueChange={(v) => { setFiltroEstado(v); setPage(1); }}>
                  <SelectTrigger className="text-sm" data-testid="filtro-estado">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    {estadosFiltrados.map(e => (
                      <SelectItem key={e.estado} value={e.estado}>{e.estado_label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  placeholder="Fecha desde"
                  value={filtroFechaDesde}
                  onChange={(e) => { setFiltroFechaDesde(e.target.value); setPage(1); }}
                  className="text-sm"
                  data-testid="filtro-fecha-desde"
                />
                <Input
                  type="date"
                  placeholder="Fecha hasta"
                  value={filtroFechaHasta}
                  onChange={(e) => { setFiltroFechaHasta(e.target.value); setPage(1); }}
                  className="text-sm"
                  data-testid="filtro-fecha-hasta"
                />
                <Select value={filtroOrigen} onValueChange={(v) => { setFiltroOrigen(v); setPage(1); }}>
                  <SelectTrigger className="text-sm" data-testid="filtro-origen">
                    <SelectValue placeholder="Origen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los orígenes</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="excel">Excel</SelectItem>
                    <SelectItem value="webhook_sperant">Webhook Sperant</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filtroChecklist} onValueChange={(v) => { setFiltroChecklist(v); setPage(1); }}>
                  <SelectTrigger className="text-sm" data-testid="filtro-checklist">
                    <SelectValue placeholder="Checklist" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Checklist: Todos</SelectItem>
                    <SelectItem value="incompleto">Checklist incompleto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabla de entregas */}
      <Card className="bg-white border border-[#E8E8E7]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-[#9d8272]" />
              <span className="ml-2 text-sm text-[#888]">Cargando entregas...</span>
            </div>
          ) : entregas.length === 0 ? (
            <div className="text-center py-16">
              <Handshake size={48} className="mx-auto text-[#ccc] mb-3" />
              <p className="text-[#888] text-sm">No se encontraron entregas</p>
              {hayFiltrosActivos && (
                <Button variant="link" onClick={limpiarFiltros} className="text-[#9d8272] text-sm mt-1">
                  Limpiar filtros
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Vista desktop: tabla */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="tabla-entregas">
                  <thead>
                    <tr className="border-b border-[#E8E8E7] bg-[#FAFAFA]">
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Código</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Proyecto</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Unidad P.</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Fase</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A] hidden lg:table-cell">Estado</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A] hidden lg:table-cell">Paso siguiente</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A] hidden xl:table-cell">Responsable</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A] hidden xl:table-cell">Cliente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entregas.map((entrega) => (
                      <tr
                        key={entrega.id}
                        onClick={() => navigate(`/entregas/${entrega.id}`)}
                        className="border-b border-[#E8E8E7] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                        data-testid={`entrega-row-${entrega.codigo}`}
                      >
                        <td className="p-3 font-mono text-xs font-semibold text-[#4E7A79]">
                          {entrega.codigo}
                        </td>
                        <td className="p-3 text-[#2D2D2D]">
                          {entrega.proyecto_nombre || '-'}
                        </td>
                        <td className="p-3 text-[#5A5A5A] font-mono text-xs">
                          {getUnidadCodigo(entrega)}
                        </td>
                        <td className="p-3">
                          <Badge
                            className="text-[10px] font-medium"
                            style={{
                              backgroundColor: `${getFaseColor(entrega.fase)}20`,
                              color: getFaseColor(entrega.fase),
                              borderColor: getFaseColor(entrega.fase)
                            }}
                            variant="outline"
                          >
                            {entrega.fase}
                          </Badge>
                        </td>
                        <td className="p-3 hidden lg:table-cell">
                          <span className="text-xs text-[#5A5A5A]">
                            {getEstadoLabel(entrega.estado)}
                          </span>
                        </td>
                        <td className="p-3 hidden lg:table-cell text-xs text-[#888]">
                          {entrega.paso_siguiente || '-'}
                        </td>
                        <td className="p-3 hidden xl:table-cell text-xs text-[#5A5A5A]">
                          {entrega.arquitecto_nombre || <span className="text-red-400">Sin asignar</span>}
                        </td>
                        <td className="p-3 hidden xl:table-cell text-xs text-[#5A5A5A]">
                          {entrega.cliente_nombre || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Vista mobile: cards */}
              <div className="md:hidden divide-y divide-[#E8E8E7]">
                {entregas.map((entrega) => (
                  <div
                    key={entrega.id}
                    onClick={() => navigate(`/entregas/${entrega.id}`)}
                    className="p-3 cursor-pointer hover:bg-[#FAFAFA] active:bg-[#F0F0EF] transition-colors"
                    data-testid={`entrega-card-${entrega.codigo}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-xs font-bold text-[#4E7A79]">{entrega.codigo}</span>
                      <Badge
                        className="text-[10px] font-medium"
                        style={{
                          backgroundColor: `${getFaseColor(entrega.fase)}20`,
                          color: getFaseColor(entrega.fase),
                          borderColor: getFaseColor(entrega.fase)
                        }}
                        variant="outline"
                      >
                        {entrega.fase}
                      </Badge>
                    </div>
                    <div className="text-sm text-[#2D2D2D] font-medium truncate">
                      {getUnidadCodigo(entrega)}
                    </div>
                    <div className="flex items-center justify-between mt-1.5 text-xs text-[#888]">
                      <span>{entrega.cliente_nombre || 'Sin cliente'}</span>
                      <span>{entrega.arquitecto_nombre || <span className="text-red-400">Sin asignar</span>}</span>
                    </div>
                    <div className="text-[11px] text-[#aaa] mt-1">{getEstadoLabel(entrega.estado)}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Paginación */}
          {totalPages > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-[#E8E8E7] gap-2">
              <div className="flex items-center gap-2 text-xs text-[#888]">
                <span>Mostrando {entregas.length} de {total}</span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-[70px] h-7 text-xs" data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span>por página</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="h-7 w-7 p-0"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft size={14} />
                </Button>
                <span className="text-xs px-2">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="h-7 w-7 p-0"
                  data-testid="pagination-next"
                >
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Importar */}
      <ImportarEntregasModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => { setPage(1); loadEntregas(); }}
      />
    </div>
  );
};

export default EntregasUI;
